import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { unwrapResult } from '@reduxjs/toolkit';
import dayjs, { Dayjs } from 'dayjs';
import { SelectChangeEvent } from '@mui/material';

import { dateNormalizer, errorNormalizer } from '../../../shared/Helpers/functions';
import { CustomAlert, CustomButton, CustomSelect, Picker, Text } from '../../../shared/uiComponents';
import { useCompareDates } from '../../../shared/Helpers/hooks';
import { TimeInputsWrapper } from '../../Reports/helpers';
import AdminTools from '../CalendarHeader/adminTools';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { setAddScheduledSession, setAdminAddEvent } from '../../../redux/State/clientSlice/calendarHelperSlice';
import { AdminCreateEventProperties } from '../../../redux/API/ClientAPIHelpers/calendarProperties';
import { adminCreateEvent } from '../../../redux/State/clientSlice/calendarSlice';
import { SessionType } from '../../../shared/Assets/sessionDetailsSVGs';
import { getSessionTypes } from '../../../redux/State/clientSlice/sessionSlice';
import {
	clearDiscretionarySessionState,
	getUserRemainingHours,
} from '../../../redux/State/userSlice/userSessionAllocationSlice';
import { CONFIRM_ERROR_MESSAGE_ASSESMENT } from '../../../shared/Helpers/constants';

const todayDayjs = dayjs();
const initialExpirationDate = todayDayjs.add(2, 'days');

const AddContentAdmin = () => {
	const dispatch = useDispatch<DispatchProperties>();

	const [loading, setLoading] = useState<boolean>(false);
	const [sessionTypeId, setSessionTypeId] = useState<number>(0);

	const [dateValue, setDateValue] = useState<Dayjs | null>(todayDayjs);
	const [startTimeValue, setStartTimeValue] = useState<Dayjs | null>(null);
	const [endTimeValue, setEndTimeValue] = useState<Dayjs | null>(null);
	const [expirationDateValue, setExpirationDateValue] = useState<Dayjs | null>(initialExpirationDate);
	const [confirmOverrideOpen, setConfirmOverrideOpen] = useState<boolean>(false);

	const sessionTypes = useSelector((state) => state.session.sessionTypes);
	const days = useSelector((state) => state.calendarHelper.displayedCalendarPageDays);
	const filterData = useSelector((state) => state.calendarHelper.filterData);
	const selectedDay = useSelector((state) => state.calendarHelper.selectedDay);
	const userSessionAllocation = useSelector((state) => state.userSessionAllocation.userRemainingHours);

	const { hasError, message } = useCompareDates(startTimeValue, endTimeValue);
	const userId = filterData.therapist?.id;
	const clientId = filterData.client?.id;

	useEffect(() => {
		const { day, month, year } = selectedDay;
		const selectedDateValue = new Date(year, month, day);
		const clientIdFilterValue = filterData.client?.id;
		const userIdFilterValue = filterData.therapist?.id;
		if (!clientIdFilterValue || !userIdFilterValue) return;

		dispatch(
			getSessionTypes({
				clientId: clientIdFilterValue,
				date: dateNormalizer(dayjs(selectedDateValue)),
				userId: userIdFilterValue,
			}),
		);
	}, [dispatch, filterData.client?.id, filterData.therapist?.id, selectedDay]);

	useEffect(() => {
		if (startTimeValue) {
			setEndTimeValue(startTimeValue.add(1, 'h'));
		}
	}, [startTimeValue]);

	useEffect(() => {
		const selectedDate = dayjs()
			.set('year', selectedDay.year)
			.set('month', selectedDay.month)
			.set('date', selectedDay.day);
		setDateValue(selectedDate);
	}, [selectedDay]);

	useEffect(() => {
		const userIdValue = filterData.therapist?.id;

		if (!sessionTypeId || sessionTypeId !== 9 || !userIdValue || !dateValue) return;
		const date = dateNormalizer(dateValue);
		dispatch(getUserRemainingHours({ userId: userIdValue, date, sessionType: sessionTypeId }));
	}, [dispatch, sessionTypeId, filterData.therapist?.id, dateValue]);

	const onTypeChange = (event: SelectChangeEvent<string>) => {
		const value = event.target.value;
		setSessionTypeId(parseInt(value));
	};

	const buildPayload = (force = false): AdminCreateEventProperties | null => {
		if (!startTimeValue || !endTimeValue || !days.length || !dateValue || !userId || !clientId || !expirationDateValue)
			return null;

		const hour = startTimeValue.hour();
		const minute = startTimeValue.minute();
		const startTime = dateValue.set('hour', hour).set('minute', minute).format('YYYY-MM-DDTHH:mm');

		const endHour = endTimeValue.hour();
		const endMinute = endTimeValue.minute();
		const endTime = dateValue.set('hour', endHour).set('minute', endMinute).format('YYYY-MM-DDTHH:mm');

		const data: AdminCreateEventProperties = {
			userId,
			clientId,
			date: dateNormalizer(dayjs(dateValue)),
			startTime,
			endTime,
			sessionTypeId,
			expirationDate: dateNormalizer(expirationDateValue),
		};

		return force ? { ...data, isAdminForceOperation: true } : data;
	};

	const submitPayload = (payload: AdminCreateEventProperties) => {
		setLoading(true);
		dispatch(adminCreateEvent(payload))
			.then(unwrapResult)
			.then(() => {
				toast('Created');
				dispatch(setAdminAddEvent(false));
				dispatch(setAddScheduledSession(false));
				dispatch(clearDiscretionarySessionState());
			})
			.catch((e) => {
				const responseMessage = e?.title || (typeof e === 'string' ? e : '');

				if (
					!payload.isAdminForceOperation &&
					responseMessage &&
					responseMessage.includes(CONFIRM_ERROR_MESSAGE_ASSESMENT)
				) {
					setConfirmOverrideOpen(true);
				} else {
					errorNormalizer(e);
				}
			})
			.finally(() => setLoading(false));
	};

	const addSession = () => {
		const payload = buildPayload(false);
		if (!payload) return;
		submitPayload(payload);
	};

	const isCreating = useSelector((state) => state.calendar.loading);

	return (
		<>
			<CustomAlert
				isBusy={isCreating}
				open={confirmOverrideOpen}
				title="Confirm override"
				onClose={() => setConfirmOverrideOpen(false)}
				Content={() => (
					<div>
						<Text title={CONFIRM_ERROR_MESSAGE_ASSESMENT} className="marginBottom16" />
						<div className="confirmOverrideErrorMessageAlertActions">
							<CustomButton title="Cancel" buttonType="secondaryGray" onClick={() => setConfirmOverrideOpen(false)} />
							<CustomButton
								title="Override and submit"
								onClick={() => {
									setConfirmOverrideOpen(false);
									const forced = buildPayload(true);
									if (forced) submitPayload(forced);
								}}
							/>
						</div>
					</div>
				)}
			/>
			<AdminTools />
			<CustomSelect
				label="Session type"
				data={sessionTypes}
				value={`${sessionTypeId}`}
				setValue={onTypeChange}
				className="marginBottom16"
				startAdornment={<SessionType />}
				disabled={!clientId || !userId}
				description={
					userSessionAllocation
						? `${userSessionAllocation.remainingHoursThisMonth} hours remaining out of ${userSessionAllocation.allocatedHoursPerMonth}.`
						: ''
				}
			/>
			<Picker.CustomDate label="Date of session" onChange={setDateValue} value={dateValue} className="marginBottom16" />
			<TimeInputsWrapper>
				<Picker.CustomTime label="Start time" value={startTimeValue} onChange={setStartTimeValue} />
				<Picker.CustomTime
					label="End time"
					value={endTimeValue}
					onChange={setEndTimeValue}
					error={hasError}
					errorMessage={message}
				/>
			</TimeInputsWrapper>
			<TimeInputsWrapper>
				<Picker.CustomDate label="Expiration date" value={expirationDateValue} onChange={setExpirationDateValue} />
			</TimeInputsWrapper>
			<CustomButton
				onClick={addSession}
				title="Add calendar event"
				loading={loading}
				disabled={
					!startTimeValue ||
					!endTimeValue ||
					!filterData.client?.id ||
					!filterData.therapist?.id ||
					hasError ||
					sessionTypeId === 0
				}
			/>
		</>
	);
};

export default AddContentAdmin;
